import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import type { SkillAccessibility } from "@/shared/domain/skills";

import type { ConfirmationContentProps } from "./ClientTools";
import { coerceToSkillDraft, type CreateSkillInput } from "./createSkillCoerce";
import styles from "./CreateSkillConfirmation.module.css";

const VISIBILITY_LABELS: Record<SkillAccessibility, string> = {
  private: "Private",
  org: "Org",
  "org-editable": "Org-editable",
};

/**
 * Read-only preview of the draft the model wants to seed into the create
 * editor. Runs the proposal through the same {@link coerceToSkillDraft} the
 * tool's `execute` uses, so the name, body, deduped tags, and resolved
 * visibility shown here match exactly what the editor opens with.
 *
 * The editor — not approval — is the validation seam now, so an incomplete
 * proposal still previews what it can (and the omitted fields simply don't
 * render); the user fills the rest in the editor. The only "nothing to show"
 * case is a proposal with no name, description, *and* body, where the draft
 * coerces to null and approving would error rather than open a blank form.
 */
export function CreateSkillConfirmation({ params }: ConfirmationContentProps) {
  const draft = coerceToSkillDraft(params as CreateSkillInput);

  if (draft === null) {
    return (
      <div className={styles.container}>
        <div className={styles.lead}>
          There&rsquo;s nothing to open the editor with yet — the draft needs at
          least a name, a description, or a procedure.
        </div>
      </div>
    );
  }

  const { name, description, body, accessibility, tags = [] } = draft;

  return (
    <div className={styles.container}>
      <div className={styles.lead}>Open the Skills editor with this draft?</div>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.name}>{name ?? "(unnamed)"}</div>
          {accessibility && (
            <span className={styles.visibility}>
              {VISIBILITY_LABELS[accessibility]}
            </span>
          )}
        </div>
        {description && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>When to use</span>
            <span className={styles.fieldValue}>{description}</span>
          </div>
        )}
        {body && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Procedure</span>
            <div className={styles.body}>
              <RobotoMarkdown content={body} variant="compact" />
            </div>
          </div>
        )}
        {tags.length > 0 && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Tags</span>
            <div className={styles.tags}>
              {tags.map((tag) => (
                <span key={tag} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className={styles.footnote}>
        Approving opens the Skills editor pre-filled with this draft — the skill
        isn&rsquo;t created until you review it and click Save there.
      </div>
    </div>
  );
}
